import { useCallback, useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import type { GridSelection } from '@glideapps/glide-data-grid';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

import {
  ROUTE_PORTFOLIO_TRANSACTIONS_SAVE_SUCCESS,
  ROUTE_PORTFOLIO_TRANSACTIONS_DISCARD_PORTFOLIO,
} from '@/constants/routes';

import { Nbsp } from '@/components/nbsp';
import { Button, Badge, cn } from '@simplywallst/ui-core/next';

import type { ImportedTransactions } from '../hooks';
import { useBeforeUnload, useUpdateTransactionPortfolio } from '../hooks';

import { Table, useTableValidation, useTableState } from '../components/Table';
import { ValidationError, MutationError } from '../components/Error';
import { Header } from './Header';

import type { Validation, Lang } from '../components/Table';

import {
  getErrorDetails,
  getTransactionsFromTableData,
  getTableDataFromImportedTransactions,
} from '../utils';
import { useFlag } from '@simplywallst/unleash';
import { CSVImportModal } from '../components/CSVImportModal';

const RowDeleteMobile = loadable(
  () => import('../components/RowDeleteMobile'),
  {
    resolveComponent: (module) => module.RowDeleteMobile,
  }
);

const DiscardAndDeletePortfolioModal = loadable(
  () => import('../components/DiscardAndDeletePortfolioModal'),
  {
    resolveComponent: (c) => c.DiscardAndDeletePortfolioModal,
  }
);

const BetaModal = loadable(() => import('../components/BetaModal'), {
  resolveComponent: (c) => c.BetaModal,
});

const Tooltip = ({ hasInteracted }: { hasInteracted: boolean }) => {
  const [closed, setClosed] = useState(false);

  if (closed) return null;

  return (
    <div
      data-theme="light"
      className="absolute left-[60px] top-[130px] max-w-[215px] cursor-pointer rounded-x0_75 border-solid border-subtle bg-surface-3 p-x1 text-tiny text-soft transition-opacity after:absolute after:left-5 after:top-[-10px] after:block after:size-5 after:rotate-45 after:rounded-x0_25 after:border-b-0 after:border-r-0 after:border-solid after:border-subtle after:bg-surface-3 after:content-['']"
      onTransitionEnd={() => setClosed(true)}
      style={{ opacity: hasInteracted ? 0 : 1 }}
    >
      Tip: Copy & paste your data into multiple cells at once for quick entry
    </div>
  );
};

interface Props {
  portfolioId: string;
}

export const PopulatedState = ({ portfolioId }: Props) => {
  const uploadTxEntryFlag = useFlag('mono-upload-transactions');
  const importTxModalExpFlag = useFlag('mono-portfolio-creation-import-exp');

  const canAccessImportTxModal =
    uploadTxEntryFlag.ready &&
    uploadTxEntryFlag.enabled &&
    importTxModalExpFlag.ready &&
    importTxModalExpFlag.variant.name === 'variant';

  const dispatch = useDispatch();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'megaTable',
  });

  const { isMobile, isTablet } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({
    defaultMatches: isMobile || isTablet,
  });

  useBeforeUnload();

  const { validateRows, highlightRegions } = useTableValidation();

  const [hideFooter, setHideFooter] = useState(false);
  const [gridSelection, setGridSelection] = useState<GridSelection>();
  const [selectedLanguage, setSelectedLanguage] = useState<Lang>('en-GB');
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>();
  const [showValidationError, setShowValidationError] = useState(false);
  const [showMutationError, setShowMutationError] = useState(false);
  const [validation, setValidation] = useState<Validation | null>(null);
  const [showImportTxModal, setShowImportTxModal] = useState(
    canAccessImportTxModal
  );
  const [hasShownBetaModal, setHasShownBetaModal] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(
    !canAccessImportTxModal && !hasShownBetaModal
  );

  const {
    mutate,
    status: mutationStatus,
    error: mutationError,
  } = useUpdateTransactionPortfolio({
    onSuccess: ({ updateTransactionPortfolio }) => {
      const { id } = updateTransactionPortfolio;
      dispatch({
        type: ROUTE_PORTFOLIO_TRANSACTIONS_SAVE_SUCCESS,
        payload: { portfolioId: id },
      });
    },
  });

  const tableState = useTableState([], selectedLanguage);

  const { deleteRows, getRows, replaceAllRows } = tableState;

  // Watch and reset error state when validation is successful
  useEffect(() => {
    if (showValidationError && validation?.error === null)
      setShowValidationError(false);
  }, [showValidationError, validation]);

  // Watch and reset mutation error state
  useEffect(() => {
    if (mutationStatus === 'error') return setShowMutationError(true);
    return setShowMutationError(false);
  }, [mutationStatus]);

  const handleImported = useCallback(
    (transactions: ImportedTransactions) => {
      const rows = getTableDataFromImportedTransactions(transactions);

      replaceAllRows(rows);

      const validation = validateRows(getRows());

      const { error } = validation;

      setValidation(validation);

      if (error !== null) {
        setShowValidationError(true);
      }
    },
    [replaceAllRows, getRows, validateRows]
  );

  const handleReturn = useCallback(() => {
    dispatch({
      type: ROUTE_PORTFOLIO_TRANSACTIONS_DISCARD_PORTFOLIO,
    });
  }, [dispatch]);

  const handleSave = useCallback(() => {
    const validation = validateRows(getRows());

    if (validation === null) return;

    trackEvent({
      modifier: 'save',
      action: 'click',
      transactionCount: validation.populatedRows.length,
      validationErrorCount: validation.invalidRows.length,
      portfolioId,
    });

    const { error } = validation;

    if (error !== null) {
      setValidation(validation);
      setShowValidationError(true);
      return;
    }

    if (validation.populatedRows.length === 0) return;

    const rows = validation.populatedRows.map(([, row]) => row);

    const transactions = getTransactionsFromTableData(rows);

    if (transactions.length === 0) return;

    mutate(
      { id: portfolioId, transactions },
      {
        onError: (error) => {
          const errorDetails = getErrorDetails(error);
          trackEvent({
            modifier: 'save',
            action: 'error',
            errorType: errorDetails.type,
            transactionCount: transactions.length,
            portfolioId,
          });
        },
      }
    );
  }, [validateRows, getRows, trackEvent, portfolioId, mutate]);

  const handleUpdate = useMemo(
    () => debounce(() => setValidation(validateRows(getRows())), 300),
    [validateRows, getRows]
  );

  const populatedRowCount = validation?.populatedRows.length ?? 0;
  const incompleteRowCount = validation?.invalidRows.length ?? 0;
  const ariaStatus = `${
    populatedRowCount - incompleteRowCount
  }/${populatedRowCount} transactions ready`;

  const showMobileRowDelete =
    isMobileScreen && gridSelection?.rows && gridSelection.rows.length > 0;

  return (
    <Track>
      <div className="grid h-dvh w-screen grid-rows-[max-content_auto] pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
        <Header onImportClick={() => setShowImportTxModal(true)} />
        <div
          className={cn(
            'relative grid',
            hideFooter ? 'grid-rows-[1fr]' : 'grid-rows-[1fr_max-content]'
          )}
          onMouseDown={() =>
            !showBetaModal && !hasInteracted && setHasInteracted(true)
          }
          onTouchStart={() =>
            !showBetaModal && !hasInteracted && setHasInteracted(true)
          }
        >
          <Table
            scrollToIndex={scrollToIndex}
            onUpdate={handleUpdate}
            highlightRegions={
              showValidationError ? highlightRegions : undefined
            }
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            gridSelection={gridSelection}
            setGridSelection={setGridSelection}
            onCellActivated={({ id }) => {
              if (!isMobileScreen) return;
              if (
                id === 'transactionId' ||
                id === 'cost' ||
                id === 'ticker' ||
                id === 'type'
              ) {
                return;
              }
              setHideFooter(true);
            }}
            onFinishedEditing={() => setHideFooter(false)}
            {...tableState}
          />
          {hideFooter === false && (
            <div
              className={cn(
                'grid',
                showValidationError || showMutationError
                  ? 'grid-rows-[80px_72px] sm:grid-rows-[56px_72px]'
                  : 'grid-rows-[72px]'
              )}
            >
              {showValidationError && validation ? (
                <ValidationError
                  validation={validation}
                  onDismiss={() => setShowValidationError(false)}
                  onRangeClick={(rowIndex) => setScrollToIndex(rowIndex)}
                />
              ) : showMutationError ? (
                <MutationError
                  error={mutationError}
                  onDismiss={() => setShowMutationError(false)}
                />
              ) : null}
              {showMobileRowDelete ? (
                <RowDeleteMobile
                  selectedRows={gridSelection.rows.toArray()}
                  onUnselect={() => setGridSelection(undefined)}
                  onDelete={(rows: number[]) => {
                    deleteRows(rows);
                    setGridSelection(undefined);
                    handleUpdate();
                  }}
                />
              ) : (
                <div className="grid grid-cols-[minmax(auto,_1200px)] items-center justify-center bg-surface-1">
                  <div className="grid grid-cols-[max-content_max-content] items-center justify-between px-x2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        trackEvent({
                          modifier: 'discard',
                          action: 'click',
                          transactionCount: validation?.populatedRows.length,
                          portfolioId,
                        });
                        setShowDiscardModal(true);
                      }}
                    >
                      Discard
                      <span className="hidden sm:inline-block">
                        {' '}
                        and go back
                      </span>
                    </Button>
                    <div className="grid grid-cols-[max-content_max-content] items-center justify-end gap-x2">
                      <Badge className="px-x1_75 text-xs text-solid">
                        <span role="status" aria-label={ariaStatus}>
                          <strong>
                            {`${
                              populatedRowCount - incompleteRowCount
                            } / ${populatedRowCount}`}
                          </strong>
                          <Nbsp />
                          <Nbsp />
                          <span className="text-soft">
                            <span className="hidden sm:inline-block">
                              transactions
                              <Nbsp />
                            </span>
                            ready
                          </span>
                        </span>
                      </Badge>
                      <Button
                        variant="primary"
                        className="min-w-[100px]"
                        isDisabled={showValidationError}
                        isLoading={mutationStatus === 'pending'}
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <Tooltip hasInteracted={hasInteracted} />
          {showDiscardModal && (
            <DiscardAndDeletePortfolioModal
              portfolioId={portfolioId}
              onSuccess={() => {
                trackEvent({
                  subject: 'discardModal',
                  modifier: 'confirm',
                  action: 'click',
                });
                handleReturn();
              }}
              onClose={() => {
                trackEvent({
                  subject: 'discardModal',
                  modifier: 'cancel',
                  action: 'click',
                });
                setShowDiscardModal(false);
              }}
            />
          )}
          {showImportTxModal ? (
            <CSVImportModal
              populatedRowCount={populatedRowCount}
              incompleteRowCount={incompleteRowCount}
              onClose={() => {
                setShowImportTxModal(false);
                if (!hasShownBetaModal) {
                  setShowBetaModal(true);
                  setHasShownBetaModal(true);
                }
              }}
              onSuccess={handleImported}
            />
          ) : null}
          {showBetaModal && (
            <BetaModal
              onClose={() => {
                trackEvent({
                  subject: 'betaModal',
                  modifier: 'cancel',
                  action: 'click',
                });
                setShowBetaModal(false);
                setHasShownBetaModal(true);
              }}
            />
          )}
        </div>
      </div>
    </Track>
  );
};
